import { INewsContentService } from '../services/i-news-content-service';
import { Language } from '../dto/language';
import { TranslationMode } from '../dto/translation-mode';
import { NewsTitle, NewsTranslationTitleRequest } from '../dto/news-titles';
import { MakeReactive, noopMakeReactive } from '../ui/make-reactive';

interface TitlesCacheEntry {
	items: NewsTitle[];
	nextBatchKey?: string;
	hasMore: boolean;
	loadedPages: number;
	timestamp: number;
}

interface NewsTitlesStoreState {
	cache: Map<string, TitlesCacheEntry>;
	isLoading: boolean;
	error: string;
}

/**
 * Cache TTL: 5 minutes
 */
const CACHE_TTL = 5 * 60 * 1000;

/**
 * Maximum pages to load
 */
const MAX_PAGES = 15;

/**
 * Store for managing news titles (list of articles) with caching and pagination
 */
export class NewsTitlesStore {
	private state: NewsTitlesStoreState;
	private service: INewsContentService;

	constructor(
			service: INewsContentService,
			makeReactive: MakeReactive<unknown> = noopMakeReactive
	) {
		this.service = service;
		this.state = makeReactive({
			cache: new Map<string, TitlesCacheEntry>(),
			isLoading: false,
			error: ''
		}) as NewsTitlesStoreState;
	}

	private createCacheKey(
			sourceLang: Language,
			targetLang: Language,
			categoryId: string,
			mode: TranslationMode
	): string {
		return `${sourceLang}_${targetLang}_${categoryId}_${mode}`;
	}

	private isCacheValid(entry: TitlesCacheEntry): boolean {
		return Date.now() - entry.timestamp < CACHE_TTL;
	}

	/**
	 * Fetch latest news titles for a category (first page).
	 * Returns cached data if the cache is still valid (within TTL).
	 */
	async fetchLatestArticles(
			sourceLang: Language,
			targetLang: Language,
			categoryId: string,
			mode: TranslationMode
	): Promise<{
		items: NewsTitle[];
		nextBatchKey?: string;
		hasMore: boolean;
		isNewsOutdated?: boolean;
	}> {
		const cacheKey = this.createCacheKey(sourceLang, targetLang, categoryId, mode);

		const existingCached = this.state.cache.get(cacheKey);
		if (existingCached && this.isCacheValid(existingCached)) {
			return {
				items: existingCached.items,
				nextBatchKey: existingCached.nextBatchKey,
				hasMore: existingCached.hasMore,
				isNewsOutdated: false
			};
		}

		try {
			this.state.isLoading = true;
			this.state.error = '';

			const request: NewsTranslationTitleRequest = {
				sourceLang,
				targetLang,
				mode,
				categoryId
			};

			const response = await this.service.getNewsTranslations(request);

			const expiredEntry = this.state.cache.get(cacheKey);

			if (expiredEntry) {
				this.prependTitles(sourceLang, targetLang, categoryId, mode, response.items);
				expiredEntry.nextBatchKey = response.nextBatchKey;
				expiredEntry.hasMore = !!response.nextBatchKey && expiredEntry.loadedPages < MAX_PAGES;
				expiredEntry.timestamp = Date.now();

				return {
					items: expiredEntry.items,
					nextBatchKey: expiredEntry.nextBatchKey,
					hasMore: expiredEntry.hasMore,
					isNewsOutdated: response.isNewsOutdated
				};
			} else {
				// No existing cache, create new entry
				const entry: TitlesCacheEntry = {
					items: response.items,
					nextBatchKey: response.nextBatchKey,
					hasMore: !!response.nextBatchKey && 1 < MAX_PAGES,
					loadedPages: 1,
					timestamp: Date.now()
				};
				this.state.cache.set(cacheKey, entry);

				return {
					items: response.items,
					nextBatchKey: response.nextBatchKey,
					hasMore: entry.hasMore,
					isNewsOutdated: response.isNewsOutdated
				};
			}

		} catch (error) {
			const errorMessage = error instanceof Error ? error.message:'Unknown error loading news titles';
			this.state.error = errorMessage;
			throw error;
		} finally {
			this.state.isLoading = false;
		}
	}

	/**
	 * Load more news titles (pagination)
	 */
	async loadMoreTitles(
			sourceLang: Language,
			targetLang: Language,
			categoryId: string,
			mode: TranslationMode
	): Promise<{
		items: NewsTitle[];
		nextBatchKey?: string;
		hasMore: boolean;
	}> {
		const cacheKey = this.createCacheKey(sourceLang, targetLang, categoryId, mode);
		const cached = this.state.cache.get(cacheKey);

		if (!cached || !cached.nextBatchKey || cached.loadedPages >= MAX_PAGES) {
			return {
				items: [],
				hasMore: false
			};
		}

		try {
			this.state.isLoading = true;
			this.state.error = '';

			const request: NewsTranslationTitleRequest = {
				sourceLang,
				targetLang,
				mode,
				categoryId,
				batchKey: cached.nextBatchKey
			};

			const response = await this.service.getNewsTranslations(request);

			// Append to cached items
			cached.items.push(...response.items);
			cached.nextBatchKey = response.nextBatchKey;
			cached.loadedPages += 1;
			cached.hasMore = !!response.nextBatchKey && cached.loadedPages < MAX_PAGES;
			cached.timestamp = Date.now();

			return {
				items: response.items,
				nextBatchKey: response.nextBatchKey,
				hasMore: cached.hasMore
			};

		} catch (error) {
			const errorMessage = error instanceof Error ? error.message:'Unknown error loading more titles';
			this.state.error = errorMessage;
			throw error;
		} finally {
			this.state.isLoading = false;
		}
	}

	/**
	 * Get cached items (if available)
	 */
	getCachedTitles(
			sourceLang: Language,
			targetLang: Language,
			categoryId: string,
			mode: TranslationMode
	): NewsTitle[] | null {
		const cacheKey = this.createCacheKey(sourceLang, targetLang, categoryId, mode);
		const cached = this.state.cache.get(cacheKey);

		if (cached && this.isCacheValid(cached)) {
			return cached.items;
		}

		return null;
	}

	/**
	 * Prepend new items to cache (e.g., after sync completes)
	 */
	prependTitles(
			sourceLang: Language,
			targetLang: Language,
			categoryId: string,
			mode: TranslationMode,
			newItems: NewsTitle[]
	): void {
		const cacheKey = this.createCacheKey(sourceLang, targetLang, categoryId, mode);
		const cached = this.state.cache.get(cacheKey);

		if (cached) {
			// Remove duplicates
			const existingIds = new Set(cached.items.map(item => item.translationId));
			const uniqueNewItems = newItems.filter(item => !existingIds.has(item.translationId));

			// Prepend new items
			cached.items = [...uniqueNewItems, ...cached.items];
			cached.timestamp = Date.now();
		}
	}

	/**
	 * Mark a cache entry as expired so the next fetch will hit the network
	 * but still find the existing items for prepending.
	 */
	expireCache(
			sourceLang: Language,
			targetLang: Language,
			categoryId: string,
			mode: TranslationMode
	): void {
		const cacheKey = this.createCacheKey(sourceLang, targetLang, categoryId, mode);
		const entry = this.state.cache.get(cacheKey);
		if (entry) {
			entry.timestamp = 0;
		}
	}

	/**
	 * Clear cache for specific category
	 */
	clearCache(
			sourceLang: Language,
			targetLang: Language,
			categoryId: string,
			mode: TranslationMode
	): void {
		const cacheKey = this.createCacheKey(sourceLang, targetLang, categoryId, mode);
		this.state.cache.delete(cacheKey);
	}

	/**
	 * Clear all cache
	 */
	clearAllCache(): void {
		this.state.cache.clear();
	}

	/**
	 * Check if currently loading
	 */
	isLoading(): boolean {
		return this.state.isLoading;
	}

	/**
	 * Get last error
	 */
	getError(): string {
		return this.state.error;
	}

	/**
	 * Get all cache entries (for testing/debugging)
	 */
	getAllCacheEntries(): Array<{ key: string; items: NewsTitle[]; hasMore: boolean }> {
		return Array.from(this.state.cache.entries()).map(([key, entry]) => ({
			key,
			items: entry.items,
			hasMore: entry.hasMore
		}));
	}

	public format(): string {
		const cacheEntries: string[] = [];
		const entries = this.getAllCacheEntries();
		for (const entry of entries) {
			const itemsText = entry.items
					.slice(0, 10)
					.map(item => `- ${item.title.source} (${item.date})`)
					.join('\n');

			cacheEntries.push(
					`cache (${entry.key}): ${entry.items.length} items, hasMore: ${entry.hasMore}`,
					itemsText
			);
		}

		return cacheEntries.join('\n') || 'cache: (empty)';
	}
}
